import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAssinarDocumentoMutation } from "@/hooks/useAssinaturas";
import { useMutation } from "@tanstack/react-query";
import { Eye, FileSignature, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Anexos from "../components/anexos";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { ConsultaOftalmologicaProps } from "../interfaces/consultaOftalmologica.interface";
import { ConsultaOftalmologicaFormData, getInitialData } from "../models/consultaOftalmologica.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const ConsultaOftalmologica = ({ onDataChange, initialData, onSave, onPrint, atendimentoId, pacienteId, empresaId }: ConsultaOftalmologicaProps) => {
  const [formData, setFormData] = useState<ConsultaOftalmologicaFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);
  const [quantidadeExames, setQuantidadeExames] = useState<number>(10); // Limite padrão de 10 para Consulta Oftalmológica (não requer exames)
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Atualizar protocoloId quando initialData mudar
  useEffect(() => {
    if (initialData?.id) {
      setProtocoloId(initialData.id);
    }
  }, [initialData?.id]);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: ConsultaOftalmologicaFormData) => {
      // Validar campos obrigatórios
      if (!pacienteId) {
        throw new Error('ID do paciente é obrigatório');
      }
      if (!atendimentoId) {
        throw new Error('ID do atendimento é obrigatório');
      }

      // Remover campos null/undefined do data antes de enviar
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
      ) as ConsultaOftalmologicaFormData;

      // Preparar dados para envio - garantir que status_atendimento só seja incluído se for string válida
      const dataToSave: any = {
        ...cleanData,
        id_paciente: pacienteId,
        id_atendimento: atendimentoId
      };

      // Incluir id_empresa apenas se fornecido
      if (empresaId) {
        dataToSave.id_empresa = empresaId;
      }

      // Incluir status_atendimento apenas se for string válida (não null, não undefined, não vazio)
      if (statusAtendimento && typeof statusAtendimento === 'string' && statusAtendimento.trim()) {
        dataToSave.status_atendimento = statusAtendimento;
      }

      return await saveFormProtocolo({
        endpoint: '/api/protocolo-consulta-oftalmologica',
        data: dataToSave,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId,
        empresaId: empresaId,
        protocoloId: protocoloId || initialData?.id,
        statusAtendimento: statusAtendimento && typeof statusAtendimento === 'string' ? statusAtendimento : undefined,
        formName: 'Consulta Oftalmológica'
      });
    },
    onSuccess: (response) => {
      // Atualizar o protocoloId com o ID retornado
      if (response?.result?.id) {
        setProtocoloId(response.result.id);
        // Atualizar formData com os dados retornados
        const updatedData = getInitialData(response.result);
        setFormData(updatedData);
        // Atualizar status se presente
        if (response.result.status_atendimento) {
          setStatusAtendimento(response.result.status_atendimento);
        }
        // Atualizar o componente pai através do onDataChange
        if (onDataChange) {
          onDataChange({
            ...updatedData,
            id: response.result.id,
            id_paciente: response.result.id_paciente,
            id_atendimento: response.result.id_atendimento,
            id_empresa: response.result.id_empresa
          } as any);
        }
      } else if (statusAtendimento) {
        // Fallback: apenas atualizar status se não houver resposta completa
        setFormData((prevData) => ({
          ...prevData,
          status_atendimento: statusAtendimento
        }));
      }
      
      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar consulta oftalmológica:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: string | boolean) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [field]: value };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }

    // Definir campos obrigatórios para Consulta Oftalmológica
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    const dataToSave = statusAtendimento 
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando consulta oftalmológica:', { endpoint: '/api/protocolo-consulta-oftalmologica', data: dataToSave, atendimentoId });
      await saveMutation.mutateAsync(dataToSave);
      console.log('Consulta oftalmológica salva com sucesso');
    } catch (error: any) {
      console.error('Erro ao salvar consulta oftalmológica:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, formData, statusAtendimento, saveMutation]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-consulta-oftalmologica/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "consulta_oftalmologica",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const assinarMutation = useAssinarDocumentoMutation();
  const [salvarEAssinarPending, setSalvarEAssinarPending] = useState(false);

  const handleSalvarEAssinar = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }
    const camposObrigatorios: CampoObrigatorio[] = [];
    const dataToSave = statusAtendimento
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) return;
    setSalvarEAssinarPending(true);
    try {
      const res = await saveMutation.mutateAsync(dataToSave);
      const idProtocolo = res?.result?.id ?? protocoloId ?? initialData?.id;
      if (!idProtocolo) {
        toast.error("Não foi possível obter o ID do protocolo após salvar.");
        return;
      }
      await assinarMutation.mutateAsync({
        prontuarioId: atendimentoId,
        protocoloId: idProtocolo,
        tipoProtocolo: "consulta_oftalmologica"
      });
      if (res?.result?.id) setProtocoloId(res.result.id);
      toast.success("Formulário salvo e documento assinado com sucesso!");
    } catch (err: any) {
      toast.error(err?.message || "Erro ao salvar e assinar.");
    } finally {
      setSalvarEAssinarPending(false);
    }
  }, [atendimentoId, formData, statusAtendimento, protocoloId, initialData?.id, saveMutation, assinarMutation]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
        <Button
          variant="secondary"
          onClick={handleSalvarEAssinar}
          disabled={saveMutation.isPending || salvarEAssinarPending}
        >
          {(saveMutation.isPending || salvarEAssinarPending) ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileSignature className="h-4 w-4 mr-2" />
          )}
          Salvar e assinar
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Consulta Oftalmológica"
        value={formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          handleInputChange('status_atendimento', status || '');
        }}
      />
      <div>
        <Label>Sem correção:</Label>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <div>
            <Label className="text-sm font-normal">OD Esf:</Label>
            <Input 
              placeholder="Esf" 
              value={formData.semCorrecaoOdEsf}
              onChange={(e) => handleInputChange('semCorrecaoOdEsf', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">Cil:</Label>
            <Input 
              placeholder="Cil" 
              value={formData.semCorrecaoOdCil}
              onChange={(e) => handleInputChange('semCorrecaoOdCil', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">OE Esf:</Label>
            <Input 
              placeholder="Esf" 
              value={formData.semCorrecaoOeEsf}
              onChange={(e) => handleInputChange('semCorrecaoOeEsf', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">Cil:</Label>
            <Input 
              placeholder="Cil" 
              value={formData.semCorrecaoOeCil}
              onChange={(e) => handleInputChange('semCorrecaoOeCil', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div>
        <Label>Com correção:</Label>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <div>
            <Label className="text-sm font-normal">OD Esf:</Label>
            <Input 
              placeholder="Esf" 
              value={formData.comCorrecaoOdEsf}
              onChange={(e) => handleInputChange('comCorrecaoOdEsf', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">Cil:</Label>
            <Input 
              placeholder="Cil" 
              value={formData.comCorrecaoOdCil}
              onChange={(e) => handleInputChange('comCorrecaoOdCil', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">OE Esf:</Label>
            <Input 
              placeholder="Esf" 
              value={formData.comCorrecaoOeEsf}
              onChange={(e) => handleInputChange('comCorrecaoOeEsf', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-sm font-normal">Cil:</Label>
            <Input 
              placeholder="Cil" 
              value={formData.comCorrecaoOeCil}
              onChange={(e) => handleInputChange('comCorrecaoOeCil', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>PIO OD (Pressão Intraocular)</Label>
          <Input 
            placeholder="Ex: 14 mmHg" 
            value={formData.pioOd}
            onChange={(e) => handleInputChange('pioOd', e.target.value)}
          />
        </div>
        <div>
          <Label>PIO OE (Pressão Intraocular)</Label>
          <Input 
            placeholder="Ex: 15 mmHg" 
            value={formData.pioOe}
            onChange={(e) => handleInputChange('pioOe', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Biomicroscopia OD</Label>
          <Textarea 
            placeholder="Descrição OD..." 
            rows={3} 
            value={formData.biomicroscopiaOd}
            onChange={(e) => handleInputChange('biomicroscopiaOd', e.target.value)}
          />
        </div>
        <div>
          <Label>Biomicroscopia OE</Label>
          <Textarea 
            placeholder="Descrição OE..." 
            rows={3} 
            value={formData.biomicroscopiaOe}
            onChange={(e) => handleInputChange('biomicroscopiaOe', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Gonioscopia</Label>
        <Textarea 
          placeholder="Descrever achados da gonioscopia..." 
          rows={3} 
          value={formData.gonioscopia}
          onChange={(e) => handleInputChange('gonioscopia', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fundoscopia OD</Label>
          <Textarea 
            placeholder="Descrição OD..." 
            rows={3} 
            value={formData.fundoscopiaOd}
            onChange={(e) => handleInputChange('fundoscopiaOd', e.target.value)}
          />
        </div>
        <div>
          <Label>Fundoscopia OE</Label>
          <Textarea 
            placeholder="Descrição OE..." 
            rows={3} 
            value={formData.fundoscopiaOe}
            onChange={(e) => handleInputChange('fundoscopiaOe', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tonometria de aplanação OD</Label>
          <Textarea 
            placeholder="Descrição OD..." 
            rows={2} 
            value={formData.tonometriaAplanacaoOd}
            onChange={(e) => handleInputChange('tonometriaAplanacaoOd', e.target.value)}
          />
        </div>
        <div>
          <Label>Tonometria de aplanação OE</Label>
          <Textarea 
            placeholder="Descrição OE..." 
            rows={2} 
            value={formData.tonometriaAplanacaoOe}
            onChange={(e) => handleInputChange('tonometriaAplanacaoOe', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Campo visual OD</Label>
          <Textarea 
            placeholder="Descrição OD..." 
            rows={2} 
            value={formData.campoVisualOd}
            onChange={(e) => handleInputChange('campoVisualOd', e.target.value)}
          />
        </div>
        <div>
          <Label>Campo visual OE</Label>
          <Textarea 
            placeholder="Descrição OE..." 
            rows={2} 
            value={formData.campoVisualOe}
            onChange={(e) => handleInputChange('campoVisualOe', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Limitação:</Label>
        <div className="flex gap-6 mt-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="limitacao-cognitiva"
              checked={formData.limitacaoCognitiva}
              onCheckedChange={(checked) => handleInputChange('limitacaoCognitiva', checked === true)}
            />
            <Label htmlFor="limitacao-cognitiva" className="font-normal cursor-pointer">
              Cognitiva
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="limitacao-locomocao"
              checked={formData.limitacaoLocomocao}
              onCheckedChange={(checked) => handleInputChange('limitacaoLocomocao', checked === true)}
            />
            <Label htmlFor="limitacao-locomocao" className="font-normal cursor-pointer">
              Locomoção
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="limitacao-visao"
              checked={formData.limitacaoVisao}
              onCheckedChange={(checked) => handleInputChange('limitacaoVisao', checked === true)}
            />
            <Label htmlFor="limitacao-visao" className="font-normal cursor-pointer">
              Visão
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="limitacao-audicao"
              checked={formData.limitacaoAudicao}
              onCheckedChange={(checked) => handleInputChange('limitacaoAudicao', checked === true)}
            />
            <Label htmlFor="limitacao-audicao" className="font-normal cursor-pointer">
              Audição
            </Label>
          </div>
        </div>
      </div>

      <div>
        <Label>Outros</Label>
        <Textarea 
          placeholder="Descrição..." 
          rows={2} 
          value={formData.outros}
          onChange={(e) => handleInputChange('outros', e.target.value)}
        />
      </div>

      <div>
        <Label>Alergia</Label>
        <Textarea 
          placeholder="Descrição..." 
          rows={2} 
          value={formData.alergia}
          onChange={(e) => handleInputChange('alergia', e.target.value)}
        />
      </div>

      <div>
        <Label>Histórico de baixa visão</Label>
        <Textarea 
          placeholder="Descrição..." 
          rows={2} 
          value={formData.historicoBaixaVisao}
          onChange={(e) => handleInputChange('historicoBaixaVisao', e.target.value)}
        />
      </div>

      <div>
        <div className="flex items-center space-x-2 mb-2">
          <Label>Realiza algum tratamento?</Label>
          <Switch 
            checked={formData.realizaTratamento}
            onCheckedChange={(checked) => handleInputChange('realizaTratamento', checked)}
          />
          <span className="text-sm text-muted-foreground">
            {formData.realizaTratamento ? 'Sim' : 'Não'}
          </span>
        </div>
        {formData.realizaTratamento && (
          <div className="mt-2">
            <Label>Qual?</Label>
            <Textarea 
              placeholder="Descrição do tratamento..." 
              rows={2} 
              value={formData.qualTratamento}
              onChange={(e) => handleInputChange('qualTratamento', e.target.value)}
            />
          </div>
        )}
      </div>

      <div>
        <Label>Quais medicamentos utiliza?</Label>
        <Textarea 
          placeholder="Descrição..." 
          rows={2} 
          value={formData.medicamentosUtiliza}
          onChange={(e) => handleInputChange('medicamentosUtiliza', e.target.value)}
        />
      </div>

      <div>
        <Label>Faz algum acompanhamento oftalmológico?</Label>
        <Textarea 
          placeholder="Descrição..." 
          rows={2} 
          value={formData.acompanhamentoOftalmologico}
          onChange={(e) => handleInputChange('acompanhamentoOftalmologico', e.target.value)}
        />
      </div>

      <div>
        <Label>Observações</Label>
        <Textarea 
          placeholder="Observações adicionais..." 
          rows={3} 
          value={formData.observacoes}
          onChange={(e) => handleInputChange('observacoes', e.target.value)}
        />
      </div>

      <div className="mt-6 pt-6 border-t">
        <Anexos 
          protocoloId={protocoloId || initialData?.id}
          quantidadeExames={quantidadeExames}
          endpointBase="/api/protocolo-consulta-oftalmologica"
        />
      </div>

      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`consulta_oftalmologica_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default ConsultaOftalmologica;
